// TBR-1.2 — pure helpers shared by the branding panels.
//
// Separate module rather than exports beside the components: mixing the two breaks React
// Fast Refresh for the whole file (react-refresh/only-export-components), and these are the
// bits worth unit-testing anyway.

import { PRESET_THEMES, type SEOConfig, type ThemeConfig } from '@/types/theme';
import { getColorContrast } from '@/utils/themeUtils';

export type SchemeOrigin = 'builtin' | 'custom';

/** One card in the scheme gallery. */
export interface SchemeCard {
  id: string;
  name: string;
  description: string;
  origin: SchemeOrigin;
  /** primary (rendered wide), then secondary / accent / sidebar. */
  swatches: [string, string, string, string];
  /** True for the tenant's live scheme — the `In use` pill. */
  isActive: boolean;
  theme: ThemeConfig;
}

export interface SchemeGalleryModel {
  builtIn: SchemeCard[];
  yours: SchemeCard[];
}

function toCard(
  theme: ThemeConfig,
  origin: SchemeOrigin,
  activeSchemeId: string | null | undefined,
): SchemeCard {
  return {
    id: theme.id,
    name: theme.name,
    description: theme.description ?? '',
    origin,
    swatches: [
      theme.colors?.primary ?? '#000000',
      theme.colors?.secondary ?? '#000000',
      theme.colors?.accent ?? '#000000',
      theme.colors?.sidebarColor ?? '#000000',
    ],
    isActive: theme.id === activeSchemeId,
    theme,
  };
}

/**
 * One gallery, two origin groups.
 *
 * Built-ins and the tenant's own schemes used to live on separate tabs ("Themes" and
 * "Custom"). They are the same kind of thing to someone picking one, so they share a grid and
 * origin survives as a pill. The split is a heading, not a navigation step.
 */
export function buildSchemeGallery(
  customThemes: ThemeConfig[] | null | undefined,
  activeSchemeId: string | null | undefined,
  presets: readonly ThemeConfig[] = PRESET_THEMES,
): SchemeGalleryModel {
  return {
    builtIn: presets.map((t) => toCard(t, 'builtin', activeSchemeId)),
    yours: (customThemes ?? []).map((t) => toCard(t, 'custom', activeSchemeId)),
  };
}

/** How the logo is handled in dark mode. Mirrors tenant_theme_settings.logo_dark_treatment. */
export type LogoDarkTreatment = 'same' | 'invert' | 'custom';

/** The CSS filter that makes a solid-color mark work on a dark ground. */
export const LOGO_INVERT_FILTER = 'invert(1) hue-rotate(180deg)';

export interface ResolvedLogo {
  url: string | null;
  /** True when the caller must apply LOGO_INVERT_FILTER to the image. */
  invert: boolean;
}

/** Anything unrecognised (including undefined, pre-migration) reads as 'same'. */
export function normalizeLogoDarkTreatment(value: string | null | undefined): LogoDarkTreatment {
  return value === 'invert' || value === 'custom' ? value : 'same';
}

function trimmed(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

/**
 * Which logo the app chrome should render, and whether to invert it.
 *
 * Three treatments, because two of them are genuinely useful and picking automatically between
 * them is what went wrong before:
 *
 *   same    the light logo unchanged. Correct for a mark that reads on either ground.
 *   invert  the light logo through invert(1) hue-rotate(180deg). Invert flips lightness and the
 *           hue rotation puts the hue back, so a SOLID-COLOR mark comes out looking deliberate.
 *           This used to be applied to every logo unconditionally, which mangled anything with
 *           more than one color. As a choice with a preview it is the cheapest good answer.
 *   custom  a separately uploaded dark variant. Always right, always more work.
 *
 * The treatment alone decides, so an uploaded image never silently overrides a chosen 'invert'.
 * 'custom' with nothing uploaded degrades to 'same' rather than rendering no logo at all.
 */
export function resolveAppLogo(
  lightLogo: string | null | undefined,
  darkLogo: string | null | undefined,
  treatment: string | null | undefined,
  isDarkMode: boolean,
): ResolvedLogo {
  const light = trimmed(lightLogo);
  if (!isDarkMode) return { url: light, invert: false };

  switch (normalizeLogoDarkTreatment(treatment)) {
    case 'custom':
      return { url: trimmed(darkLogo) ?? light, invert: false };
    case 'invert':
      // Nothing to invert when there is no logo, and inverting the fallback mark would be a
      // surprise rather than a preference.
      return { url: light, invert: light !== null };
    default:
      return { url: light, invert: false };
  }
}

/** Fields an admin has actually filled in — drives the "5 fields set" SEO & Social row. */
export function countSeoFieldsSet(seo: SEOConfig | null | undefined): number {
  if (!seo) return 0;
  return Object.values(seo).filter((v) => typeof v === 'string' && v.trim() !== '').length;
}

/**
 * Sidebar color that adapts to the active palette.
 *
 * Third copy of this function (ThemeContext.tsx:57, ThemeTab's original body). All three
 * were byte-identical and the other two predate this story, so unifying them is a separate
 * change with its own blast radius — this one exists so the branding panel does not add a
 * fourth inline copy.
 */
export function getAdaptiveSidebarColor(primary: string, background: string): string {
  const backgroundIsLight = getColorContrast(background) === 'light';
  if (backgroundIsLight) {
    // Dark sidebar on a light page: use primary if it is dark enough, else a dark neutral.
    return getColorContrast(primary) === 'dark' ? primary : '#1f2937';
  }
  return getColorContrast(primary) === 'light' ? primary : '#f3f4f6';
}
